'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    getGlosses,
    getGlossStats,
    Gloss,
    GlossStats,
    GLOSS_STATUS_LABELS,
    GLOSS_STATUS_COLORS,
    GLOSS_TYPE_LABELS,
} from '@/lib/api/billing/gloss-api'
import {
    MdWarning,
    MdSearch,
    MdClose,
    MdArrowBack,
    MdArrowForward,
    MdDescription, MdTrendingUp
} from 'react-icons/md'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

export default function GlossesPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const [glosses, setGlosses] = useState<Gloss[]>([])
    const [stats, setStats] = useState<GlossStats | null>(null)
    const [loading, setLoading] = useState(true)
    const [showSearch, setShowSearch] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [typeFilter, setTypeFilter] = useState<string>('all')

    useEffect(() => {
        if (user?.companyId) {
            loadData()
        }
    }, [user?.companyId])

    const loadData = async () => {
        setLoading(true)
        try {
            const [glossesResponse, statsResponse] = await Promise.all([
                getGlosses(),
                getGlossStats(),
            ])

            if (glossesResponse.success) {
                setGlosses(glossesResponse.result || [])
            }
            if (statsResponse.success) {
                setStats(statsResponse.result)
            }
        } catch (error) {
            toast.error('Erro ao carregar dados')
        } finally {
            setLoading(false)
        }
    }

    const filteredGlosses = glosses.filter(gloss => {
        if (searchTerm) {
            const search = searchTerm.toLowerCase()
            if (
                !gloss.glossCode.toLowerCase().includes(search) &&
                !gloss.glossDescription.toLowerCase().includes(search) &&
                !gloss.guide?.guideNumber?.toLowerCase().includes(search)
            ) {
                return false
            }
        }
        if (statusFilter !== 'all' && gloss.status !== statusFilter) {
            return false
        }
        if (typeFilter !== 'all' && gloss.type !== typeFilter) {
            return false
        }
        return true
    })

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value)
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando glosas...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/billing')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-lg" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg">
                        <MdWarning className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Glosas
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {glosses.length}{' '}
                            {glosses.length === 1 ? 'glosa' : 'glosas'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {showSearch ? (
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                placeholder="Buscar glosas..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="input input-bordered input-sm w-64"
                                autoFocus
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    setShowSearch(false)
                                    setSearchTerm('')
                                }}
                                className="btn btn-sm btn-ghost"
                            >
                                <MdClose className="text-lg" />
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => setShowSearch(true)}
                            className="btn btn-sm btn-ghost gap-2"
                        >
                            <MdSearch className="text-lg" />
                            <span className="hidden sm:inline">Buscar</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-4">
                        <p className="text-xs text-base-content/60 mb-1">
                            Total Glosado
                        </p>
                        <p className="text-xl font-bold text-error">
                            {formatCurrency(stats.totalValue)}
                        </p>
                    </div>
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-4">
                        <p className="text-xs text-base-content/60 mb-1">
                            Total Recuperado
                        </p>
                        <p className="text-xl font-bold text-success">
                            {formatCurrency(stats.totalRecovered)}
                        </p>
                    </div>
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-4">
                        <p className="text-xs text-base-content/60 mb-1">
                            Pendentes
                        </p>
                        <p className="text-xl font-bold text-warning">
                            {stats.byStatus?.GLOSS_PENDING || 0}
                        </p>
                    </div>
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-4">
                        <p className="text-xs text-base-content/60 mb-1">
                            Taxa de Recuperação
                        </p>
                        <div className="flex items-center gap-2">
                            <MdTrendingUp className="text-success" />
                            <p className="text-xl font-bold">
                                {stats.recoveryRate.toFixed(1)}%
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="flex gap-2 flex-wrap">
                <button
                    onClick={() => setStatusFilter('all')}
                    className={`btn btn-sm ${statusFilter === 'all' ? 'btn-secondary' : 'btn-ghost'}`}
                >
                    Todas
                </button>
                <button
                    onClick={() => setStatusFilter('GLOSS_PENDING')}
                    className={`btn btn-sm ${statusFilter === 'GLOSS_PENDING' ? 'btn-warning' : 'btn-ghost'}`}
                >
                    Pendentes
                </button>
                <button
                    onClick={() => setStatusFilter('GLOSS_APPEALED')}
                    className={`btn btn-sm ${statusFilter === 'GLOSS_APPEALED' ? 'btn-info' : 'btn-ghost'}`}
                >
                    Em Recurso
                </button>
                <button
                    onClick={() => setStatusFilter('GLOSS_ACCEPTED')}
                    className={`btn btn-sm ${statusFilter === 'GLOSS_ACCEPTED' ? 'btn-success' : 'btn-ghost'}`}
                >
                    Aceitas
                </button>
                <button
                    onClick={() => setStatusFilter('GLOSS_REJECTED')}
                    className={`btn btn-sm ${statusFilter === 'GLOSS_REJECTED' ? 'btn-error' : 'btn-ghost'}`}
                >
                    Rejeitadas
                </button>
            </div>

            <div className="flex gap-4 flex-wrap">
                <select
                    value={typeFilter}
                    onChange={e => setTypeFilter(e.target.value)}
                    className="select select-bordered select-sm"
                >
                    <option value="all">Todos os Tipos</option>
                    <option value="GLOSS_ADMINISTRATIVE">Administrativa</option>
                    <option value="GLOSS_TECHNICAL">Técnica</option>
                    <option value="GLOSS_LINEAR">Linear</option>
                </select>
            </div>

            {filteredGlosses.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-12 text-center">
                    <MdWarning className="text-6xl text-base-content/20 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-base-content mb-2">
                        Nenhuma glosa encontrada...
                    </h3>
                    <p className="text-base-content/60">
                        As glosas serão exibidas aqui quando registradas
                    </p>
                </div>
            ) : (
                <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-300/50">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Guia</th>
                                <th>Código</th>
                                <th>Tipo</th>
                                <th>Valor Glosado</th>
                                <th>Status</th>
                                <th>Prazo Recurso</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredGlosses.map(gloss => (
                                <tr
                                    key={gloss.id}
                                    className="hover cursor-pointer"
                                    onClick={() =>
                                        navigate(`/billing/glosses/${gloss.id}`)
                                    }
                                >
                                    <td>
                                        <div className="flex items-center gap-2">
                                            <MdDescription className="text-base-content/40" />
                                            <span>{gloss.guide?.guideNumber}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div>
                                            <div className="font-semibold">
                                                {gloss.glossCode}
                                            </div>
                                            <div className="text-xs text-base-content/60 max-w-xs truncate">
                                                {gloss.glossDescription}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="badge badge-ghost badge-sm">
                                            {GLOSS_TYPE_LABELS[gloss.type]}
                                        </span>
                                    </td>
                                    <td className="font-semibold text-error">
                                        {formatCurrency(gloss.glossValue)}
                                    </td>
                                    <td>
                                        <span
                                            className={`badge badge-sm ${GLOSS_STATUS_COLORS[gloss.status]}`}
                                        >
                                            {GLOSS_STATUS_LABELS[gloss.status]}
                                        </span>
                                    </td>
                                    <td>
                                        {gloss.appealDeadline ? (
                                            <span className="text-sm">
                                                {format(
                                                    new Date(gloss.appealDeadline),
                                                    'dd/MM/yyyy',
                                                    { locale: ptBR }
                                                )}
                                            </span>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                    <td>
                                        <MdArrowForward className="text-base-content/40" />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}
